import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('docvault_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = {
  // Auth
  login: (email, password) => axios.post(`${API}/auth/login`, { email, password }),
  getMe: () => axios.get(`${API}/auth/me`, { headers: getAuthHeaders() }),
  updateLanguage: (language) => axios.put(`${API}/auth/language`, { language }, { headers: getAuthHeaders() }),
  updatePassword: (data) => axios.put(`${API}/auth/password`, data, { headers: getAuthHeaders() }),

  // Dashboard
  getDashboardStats: () => axios.get(`${API}/stats/dashboard`, { headers: getAuthHeaders() }),

  // Clients (Admin)
  getClients: () => axios.get(`${API}/clients`, { headers: getAuthHeaders() }),
  createClient: (data) => axios.post(`${API}/clients`, data, { headers: getAuthHeaders() }),
  updateClient: (id, data) => axios.put(`${API}/clients/${id}`, data, { headers: getAuthHeaders() }),

  // Users (Admin)
  getUsers: () => axios.get(`${API}/users`, { headers: getAuthHeaders() }),
  createUser: (data) => axios.post(`${API}/users`, data, { headers: getAuthHeaders() }),
  updateUser: (id, data) => axios.put(`${API}/users/${id}`, data, { headers: getAuthHeaders() }),
  deleteUser: (id) => axios.delete(`${API}/users/${id}`, { headers: getAuthHeaders() }),

  // Folders
  getFolders: (parentId) => {
    const params = parentId ? { parent_id: parentId } : {};
    return axios.get(`${API}/folders`, { headers: getAuthHeaders(), params });
  },
  getAllFolders: () => axios.get(`${API}/folders/all`, { headers: getAuthHeaders() }),
  getFolder: (id) => axios.get(`${API}/folders/${id}`, { headers: getAuthHeaders() }),
  createFolder: (data) => axios.post(`${API}/folders`, data, { headers: getAuthHeaders() }),
  updateFolder: (id, data) => axios.put(`${API}/folders/${id}`, data, { headers: getAuthHeaders() }),
  deleteFolder: (id) => axios.delete(`${API}/folders/${id}`, { headers: getAuthHeaders() }),

  // Documents
  getDocuments: (folderId, search) => {
    const params = {};
    if (folderId) params.folder_id = folderId;
    if (search) params.search = search;
    return axios.get(`${API}/documents`, { headers: getAuthHeaders(), params });
  },
  getRecentDocuments: (limit = 10) => axios.get(`${API}/documents/recent`, { 
    headers: getAuthHeaders(), 
    params: { limit } 
  }),
  getDocument: (id) => axios.get(`${API}/documents/${id}`, { headers: getAuthHeaders() }),
  
  // Document versioning
  checkDocumentDuplicate: (filename, folderId, fileHash) => {
    const formData = new FormData();
    formData.append('filename', filename);
    if (folderId) formData.append('folder_id', folderId);
    if (fileHash) formData.append('file_hash', fileHash);
    return axios.post(`${API}/documents/check-duplicate`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
  },
  getDocumentVersions: (id) => axios.get(`${API}/documents/${id}/versions`, { headers: getAuthHeaders() }),
  
  uploadDocument: (file, folderId, tags, updateExistingId = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) formData.append('folder_id', folderId);
    if (tags) formData.append('tags', tags);
    if (updateExistingId) formData.append('update_existing', updateExistingId);
    return axios.post(`${API}/documents`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadDocument: async (id, filename) => {
    const response = await axios.get(`${API}/documents/${id}/download`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  getDocumentPreviewUrl: (id) => {
    const token = localStorage.getItem('docvault_token');
    return `${API}/documents/${id}/preview?token=${token}`;
  },
  getDocumentPreviewPdfaUrl: (id) => {
    const token = localStorage.getItem('docvault_token');
    return `${API}/documents/${id}/preview-pdfa?token=${token}`;
  },
  getDocumentPreviewBlob: async (id) => {
    const response = await axios.get(`${API}/documents/${id}/preview`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    return response.data;
  },
  updateDocumentTags: (id, tags) => axios.put(`${API}/documents/${id}/tags`, { tags }, { headers: getAuthHeaders() }),
  moveDocument: (id, folderId) => axios.put(`${API}/documents/${id}/move`, { folder_id: folderId }, { headers: getAuthHeaders() }),
  getAllTags: () => axios.get(`${API}/tags`, { headers: getAuthHeaders() }),
  getDocumentsByTags: (tags, matchAll = false, folderId = null) => {
    const params = { tags, match_all: matchAll };
    if (folderId) params.folder_id = folderId;
    return axios.get(`${API}/documents/by-tags`, { headers: getAuthHeaders(), params });
  },
  deleteDocument: (id) => axios.delete(`${API}/documents/${id}`, { headers: getAuthHeaders() }),
  
  // PDF/A Downloads
  downloadPdfA: async (id, originalFilename) => {
    const response = await axios.get(`${API}/documents/${id}/download-pdfa`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    const pdfaFilename = originalFilename.replace(/\.[^.]+$/, '') + '_PDFA.pdf';
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', pdfaFilename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  getPdfAPreviewBlob: async (id) => {
    const response = await axios.get(`${API}/documents/${id}/preview-pdfa`, {
      headers: getAuthHeaders(),
      responseType: 'blob'
    });
    return response.data;
  },

  // Audit Logs
  getAuditLogs: (limit = 100, resourceType, dateFrom, dateTo) => {
    const params = { limit };
    if (resourceType) params.resource_type = resourceType;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return axios.get(`${API}/audit-logs`, { headers: getAuthHeaders(), params });
  },
  exportAuditLogs: async (dateFrom, dateTo, resourceType) => {
    const params = { date_from: dateFrom, date_to: dateTo };
    if (resourceType && resourceType !== 'all') params.resource_type = resourceType;
    
    const response = await axios.get(`${API}/audit-logs/export`, {
      headers: getAuthHeaders(),
      params,
      responseType: 'blob'
    });
    
    const filename = `audit_log_${dateFrom}_to_${dateTo}.csv`;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
  exportAuditArchive: async (dateFrom, dateTo, resourceType) => {
    const params = { date_from: dateFrom, date_to: dateTo };
    if (resourceType && resourceType !== 'all') params.resource_type = resourceType;
    
    const response = await axios.get(`${API}/audit-logs/export-archive`, {
      headers: getAuthHeaders(),
      params,
      responseType: 'blob'
    });
    
    const filename = `archivio_attivita_${dateFrom}_to_${dateTo}.zip`;
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Scanner / OCR
  processScannedDocument: (file, folderId, tags, language = 'it', extractInvoice = true) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folderId) formData.append('folder_id', folderId);
    if (tags) formData.append('tags', tags);
    formData.append('language', language);
    formData.append('extract_invoice', extractInvoice);
    return axios.post(`${API}/scanner/process`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
  },
  ocrPreview: (file, language = 'it', extractInvoice = true) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);
    formData.append('extract_invoice', extractInvoice);
    return axios.post(`${API}/scanner/ocr-only`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
  },
  getScannedDocuments: (folderId = null) => {
    const params = folderId ? { folder_id: folderId } : {};
    return axios.get(`${API}/scanner/documents`, { headers: getAuthHeaders(), params });
  },
  getScannedDocument: (id) => axios.get(`${API}/scanner/documents/${id}`, { headers: getAuthHeaders() }),
  updateInvoiceData: (id, invoiceData) => axios.put(`${API}/scanner/documents/${id}/invoice-data`, invoiceData, { headers: getAuthHeaders() }),

  // Workflow
  getApprovers: () => axios.get(`${API}/workflow/approvers`, { headers: getAuthHeaders() }),
  submitForApproval: (documentId, approverId, notes) => axios.post(`${API}/workflow/submit`, 
    { document_id: documentId, approver_id: approverId, notes }, { headers: getAuthHeaders() }),
  getWorkflowsPending: () => axios.get(`${API}/workflow/pending`, { headers: getAuthHeaders() }),
  getMyWorkflowRequests: () => axios.get(`${API}/workflow/my-requests`, { headers: getAuthHeaders() }),
  getWorkflowsToApprove: () => axios.get(`${API}/workflow/to-approve`, { headers: getAuthHeaders() }),
  getWorkflowCount: () => axios.get(`${API}/workflow/count`, { headers: getAuthHeaders() }),
  getWorkflow: (id) => axios.get(`${API}/workflow/${id}`, { headers: getAuthHeaders() }),
  workflowAction: (id, action, notes) => axios.post(`${API}/workflow/${id}/action`, { action, notes }, { headers: getAuthHeaders() }),
  resubmitWorkflow: (id, documentId, approverId, notes) => axios.post(`${API}/workflow/${id}/resubmit`, 
    { document_id: documentId, approver_id: approverId, notes }, { headers: getAuthHeaders() }),
  getAllWorkflows: (status) => {
    const params = status ? { status } : {};
    return axios.get(`${API}/workflow/all`, { headers: getAuthHeaders(), params });
  },

  // Init
  initData: () => axios.post(`${API}/init-data`),

  // Emails
  getEmails: (folderId, search) => {
    const params = {};
    if (folderId && folderId !== 'all') params.folder_id = folderId;
    if (search) params.search = search;
    return axios.get(`${API}/emails`, { headers: getAuthHeaders(), params });
  },
  getEmail: (id) => axios.get(`${API}/emails/${id}`, { headers: getAuthHeaders() }),
  getEmbeddedEmail: (id) => axios.get(`${API}/emails/embedded/${id}`, { headers: getAuthHeaders() }),
  getEmailAttachments: (emailId) => axios.get(`${API}/emails/${emailId}/attachments`, { headers: getAuthHeaders() }),
  uploadEmail: (file, folderId, tags) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder_id', folderId || '');
    formData.append('tags', tags || '');
    return axios.post(`${API}/emails`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteEmail: (id) => axios.delete(`${API}/emails/${id}`, { headers: getAuthHeaders() }),
  downloadEmail: (id) => {
    const token = localStorage.getItem('docvault_token');
    return `${API}/emails/${id}/download?token=${token}`;
  },
  getEmailPreviewPdfaUrl: (id) => {
    const token = localStorage.getItem('docvault_token');
    return `${API}/emails/${id}/preview-pdfa?token=${token}`;
  },
  downloadEmailAttachment: (id) => {
    const token = localStorage.getItem('docvault_token');
    return `${API}/emails/attachments/${id}/download?token=${token}`;
  },
  getEmailAttachmentPreviewPdfaUrl: (id) => {
    const token = localStorage.getItem('docvault_token');
    return `${API}/emails/attachments/${id}/preview-pdfa?token=${token}`;
  }
};

export const formatBytes = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const formatDate = (dateString, locale = 'it-CH') => {
  return new Date(dateString).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
